import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { graphql, readInlineData } from "react-relay";

import type { hooksTrackCurrentItemBase$key } from "scmp-app/queries/__generated__/hooksTrackCurrentItemBase.graphql";
import type { hooksUseCurrentArticleIdsArticle$key } from "scmp-app/queries/__generated__/hooksUseCurrentArticleIdsArticle.graphql";

import { currentArticleAtom, currentItemAtom } from "./atoms";

const currentItemFragment = graphql`
  fragment hooksTrackCurrentItemBase on BaseWithApplicationAndUrlAlias
  @inline
  @argumentDefinitions(
    customContents: { type: "[CustomContentInput]", defaultValue: [] }
    bodyType: { type: "BodyFormatTypeEnum", defaultValue: PARAGRAPH }
  ) {
    __typename
    ...atomsCurrentArticleAtomBase @arguments(customContents: $customContents, bodyType: $bodyType)
    ...atomsCurrentShareContentAtomBase
    ...atomsCurrentStyleCardCarouselWidgetSectionsAtomBase
    ...atomsCurrentTakeoverAdSectionsAtomBase
  }
`;

export const getCurrentItemData = (item?: hooksTrackCurrentItemBase$key | null) =>
  item ? readInlineData(currentItemFragment, item) : null;

export const useTrackCurrentItem = () => {
  const setCurrentContent = useSetAtom(currentItemAtom);

  const trackCurrentItem = useCallback(
    (item?: hooksTrackCurrentItemBase$key) => setCurrentContent(getCurrentItemData(item)),
    [setCurrentContent],
  );

  return { trackCurrentItem };
};

export const useCurrentArticleIds = () => {
  const currentArticle = useAtomValue(currentArticleAtom);
  if (!currentArticle) return { currentArticleId: undefined, currentArticleUuid: undefined };

  const { entityId, entityUuid } = readInlineData<hooksUseCurrentArticleIdsArticle$key>(
    graphql`
      fragment hooksUseCurrentArticleIdsArticle on Content @inline {
        entityId
        entityUuid
      }
    `,
    currentArticle,
  );

  return { currentArticleId: entityId, currentArticleUuid: entityUuid };
};
